import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { arrayOf, bool, func, number, object } from 'prop-types';
import classNames from 'classnames';

import { FormattedMessage } from '../../../util/reactIntl';
import { constructQueryParamName } from '../../../util/search';

import FilterComponent from '../FilterComponent';
import PriceInputs from '../FilterBar/PriceInputs';
import {
  narrowFilterOptions,
  searchFilterOptions,
  parseSelectedValues,
  formatFilterValue,
  narrowToValues,
  isFilterActive,
  typeaheadNoun,
} from '../FilterBar/filterBarHelpers';
import useExceptFieldPresence from '../FilterBar/useExceptFieldPresence';
import useDialogFocus from '../useDialogFocus';

import css from './FilterPanel.module.css';

// Ledger FINAL 2026-08-25: rowsMode = optionCount > 8. ≤8 = pill chips;
// >8 = type-ahead + checkbox rows.
const ROWS_MODE_THRESHOLD = 8;

/**
 * One enum/multi-enum section: "{Label} · pick any · (N when rows-mode) ·
 * Clear-when-active" head, then chips (≤8 options) or a type-ahead +
 * checkbox-row list (>8). Fields with zero present options hide entirely.
 */
const PanelField = props => {
  const { filterConfig, narrowedConfig, intl, urlQueryParams, facetsParams, onSubmitField } = props;
  const [searchTerm, setSearchTerm] = useState('');

  const fullCount = filterConfig.enumOptions?.length || 0;
  const isRowsMode = fullCount > ROWS_MODE_THRESHOLD;
  const selected = parseSelectedValues(filterConfig, urlQueryParams);
  const isActive = selected.length > 0;

  // Active-field exception: compute this field's list against the OTHER
  // filters. The panel section is only mounted while the panel is open.
  const exceptPresence = useExceptFieldPresence(
    facetsParams || urlQueryParams,
    filterConfig,
    isActive
  );

  const hasPresentOptions = (narrowedConfig.enumOptions?.length || 0) > 0;
  if (!hasPresentOptions && fullCount > 0) return null;
  const label =
    filterConfig.filterConfig?.label || filterConfig.showConfig?.label || filterConfig.key;

  const submit = values => onSubmitField(filterConfig, values);
  const toggle = optionKey =>
    submit(
      selected.includes(optionKey)
        ? selected.filter(k => k !== optionKey)
        : [...selected, optionKey]
    );

  const effectiveNarrowed =
    isActive && exceptPresence
      ? narrowToValues(filterConfig, exceptPresence, selected)
      : narrowedConfig;
  const searched = searchFilterOptions(filterConfig, searchTerm);
  const options = (searched || effectiveNarrowed).enumOptions || [];

  return (
    <div className={css.field} data-panel-field={filterConfig.key}>
      <div className={css.fieldHead}>
        <span className={css.fieldLabel}>{label}</span>
        {isRowsMode ? <span className={css.optionCount}>({fullCount})</span> : null}
        <span className={css.fieldSpacer} />
        {isActive ? (
          <button type="button" className={css.fieldClear} onClick={() => submit([])}>
            <FormattedMessage id="FilterPanel.clearField" />
          </button>
        ) : null}
      </div>

      {isRowsMode ? (
        <>
          <input
            type="search"
            className={css.typeAheadInput}
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            placeholder={intl.formatMessage(
              { id: 'FilterPanel.typeaheadPlaceholder' },
              { label: typeaheadNoun(label) }
            )}
          />
          <div className={css.scrollList}>
            {options.map(option => {
              const optionKey = option.option || option.key;
              const isSelected = selected.includes(optionKey);
              return (
                <button
                  type="button"
                  key={optionKey}
                  className={classNames(css.checkboxRow, { [css.checkboxRowSelected]: isSelected })}
                  onClick={() => toggle(optionKey)}
                >
                  <span className={classNames(css.checkbox, { [css.checkboxChecked]: isSelected })}>
                    {isSelected ? '✓' : ''}
                  </span>
                  <span className={css.checkboxLabel}>{option.label || optionKey}</span>
                </button>
              );
            })}
          </div>
        </>
      ) : (
        <div className={css.chips}>
          {options.map(option => {
            const optionKey = option.option || option.key;
            const isSelected = selected.includes(optionKey);
            return (
              <button
                type="button"
                key={optionKey}
                className={classNames(css.chip, { [css.chipSelected]: isSelected })}
                onClick={() => toggle(optionKey)}
              >
                {isSelected ? <span className={css.chipCheck}>✓</span> : null}
                {option.label || optionKey}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

/**
 * Slide-out filter panel (ledger "Filter system — FINAL 2026-08-25"): header
 * "Filters · Clear all · ✕"; enum sections as chips (≤8 options) or
 * type-ahead + checkbox rows (>8); Price = min/max inputs only (auto-apply,
 * no chips/slider/Apply); Availability = one "Show sold items only" chip;
 * footer = full-width green "Show N listings". Phone renders the same
 * anatomy as a bottom sheet. Portaled to <body> so ancestor stacking
 * contexts can't trap it under the topbar.
 */
const FilterPanel = props => {
  const {
    isOpen,
    onClose,
    filters,
    presence,
    urlQueryParams,
    initialValues,
    getHandleChangedValueFn,
    marketplaceCurrency,
    listingCategories,
    intl,
    totalItems,
    facetsParams,
    focusFieldKey,
    onToggleSoldOut,
    onClearAll,
    onManageDisableScrolling,
  } = props;

  const panelRef = useRef(null);
  useDialogFocus(panelRef, isOpen);

  // Tablet pill taps land the panel pre-scrolled to the tapped field
  const contentRef = useRef(null);
  useEffect(() => {
    if (!isOpen || !focusFieldKey) return undefined;
    const raf = requestAnimationFrame(() => {
      const box = contentRef.current;
      const el = box?.querySelector(`[data-panel-field="${CSS.escape(focusFieldKey)}"]`);
      if (box && el) box.scrollTop = Math.max(0, el.offsetTop - box.offsetTop - 8);
    });
    return () => cancelAnimationFrame(raf);
  }, [isOpen, focusFieldKey]);

  useEffect(() => {
    if (onManageDisableScrolling) onManageDisableScrolling('FilterPanel', isOpen);
    return () => {
      if (onManageDisableScrolling) onManageDisableScrolling('FilterPanel', false);
    };
  }, [isOpen, onManageDisableScrolling]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = e => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const soldActive = urlQueryParams.showSoldOut === 'true' || urlQueryParams.showSoldOut === true;
  // isFilterActive covers price configs, so no separate price term
  const activeCount =
    filters.filter(f => isFilterActive(f, urlQueryParams)).length + (soldActive ? 1 : 0);

  const onSubmitField = (filterConfig, values) => {
    const paramName = constructQueryParamName(filterConfig.key, filterConfig.scope || 'public');
    getHandleChangedValueFn(true)({ [paramName]: formatFilterValue(filterConfig, values) });
  };

  // Portal to <body>: ancestor stacking contexts otherwise trap the fixed
  // panel underneath the topbar.
  if (typeof document === 'undefined') return null;
  return ReactDOM.createPortal(
    <>
      <div className={css.overlay} onClick={onClose} />
      <div
        className={css.panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby="FilterPanel.heading"
        tabIndex={-1}
        ref={panelRef}
      >
        <div className={css.sheetHandle} />
        <div className={css.head}>
          <h2 className={css.heading} id="FilterPanel.heading">
            <FormattedMessage id="FilterPanel.heading" />
          </h2>
          <button
            type="button"
            className={classNames(css.clearButton, {
              [css.clearButtonInactive]: activeCount === 0,
            })}
            onClick={onClearAll}
          >
            <FormattedMessage id="FilterPanel.clearAll" />
          </button>
          <button
            type="button"
            className={css.closeButton}
            onClick={onClose}
            aria-label={intl.formatMessage({ id: 'FilterPanel.close' })}
          >
            ✕
          </button>
        </div>

        <div className={css.content} ref={contentRef}>
          {filters.map(filterConfig => {
            const keyId = `FilterPanel.${filterConfig.scope || 'built-in'}.${filterConfig.key}`;
            if (['enum', 'multi-enum'].includes(filterConfig.schemaType)) {
              const narrowed = narrowFilterOptions(filterConfig, presence, urlQueryParams);
              return (
                <PanelField
                  key={keyId}
                  filterConfig={filterConfig}
                  narrowedConfig={narrowed}
                  intl={intl}
                  urlQueryParams={urlQueryParams}
                  facetsParams={facetsParams}
                  onSubmitField={onSubmitField}
                />
              );
            }
            if (filterConfig.schemaType === 'price') {
              const isActive = !!urlQueryParams.price;
              return (
                <div className={css.field} key={keyId} data-panel-field="price">
                  <div className={css.fieldHead}>
                    <span className={css.fieldLabel}>
                      {filterConfig.label || <FormattedMessage id="FilterPanel.priceLabel" />}
                    </span>
                    <span className={css.fieldSpacer} />
                    {isActive ? (
                      <button
                        type="button"
                        className={css.fieldClear}
                        onClick={() => getHandleChangedValueFn(true)({ price: null })}
                      >
                        <FormattedMessage id="FilterPanel.clearField" />
                      </button>
                    ) : null}
                  </div>
                  <PriceInputs
                    value={urlQueryParams.price || null}
                    onCommit={value => getHandleChangedValueFn(true)({ price: value })}
                  />
                </div>
              );
            }
            // Anything else (dates etc.) keeps its stock plain rendering
            return (
              <FilterComponent
                key={keyId}
                idPrefix="FilterPanel"
                className={css.filter}
                config={filterConfig}
                marketplaceCurrency={marketplaceCurrency}
                urlQueryParams={urlQueryParams}
                initialValues={initialValues}
                getHandleChangedValueFn={getHandleChangedValueFn}
                listingCategories={listingCategories}
                intl={intl}
                liveEdit
                showAsPopup={false}
                isDesktop
              />
            );
          })}

          {/* Availability: one chip, consistent with the ≤8-chips rule */}
          <div className={css.field}>
            <div className={css.fieldHead}>
              <span className={css.fieldLabel}>
                <FormattedMessage id="FilterPanel.availability" />
              </span>
              <span className={css.fieldSpacer} />
              {soldActive ? (
                <button
                  type="button"
                  className={css.fieldClear}
                  onClick={() => onToggleSoldOut(false)}
                >
                  <FormattedMessage id="FilterPanel.clearField" />
                </button>
              ) : null}
            </div>
            <div className={css.chips}>
              <button
                type="button"
                className={classNames(css.chip, { [css.chipSelected]: soldActive })}
                onClick={() => onToggleSoldOut(!soldActive)}
              >
                {soldActive ? <span className={css.chipCheck}>✓</span> : null}
                <FormattedMessage id="FilterPanel.showSoldOnly" />
              </button>
            </div>
          </div>
        </div>

        <div className={css.footer}>
          <button type="button" className={css.doneButton} onClick={onClose}>
            {totalItems != null ? (
              <FormattedMessage id="FilterPanel.showResults" values={{ count: totalItems }} />
            ) : (
              <FormattedMessage id="FilterPanel.done" />
            )}
          </button>
        </div>
      </div>
    </>,
    document.body
  );
};

FilterPanel.defaultProps = {
  filters: [],
  presence: null,
  totalItems: null,
  onManageDisableScrolling: null,
};

FilterPanel.propTypes = {
  isOpen: bool.isRequired,
  onClose: func.isRequired,
  filters: arrayOf(object),
  presence: object,
  urlQueryParams: object.isRequired,
  initialValues: func.isRequired,
  getHandleChangedValueFn: func.isRequired,
  onToggleSoldOut: func.isRequired,
  onClearAll: func.isRequired,
  totalItems: number,
};

export default FilterPanel;
